import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  ClipboardList, Plus, Play, CheckCircle2, XCircle, Pause,
  AlertTriangle, Clock, Warehouse, Filter,
} from 'lucide-react'
import { api, formatDateTime, formatMoney } from '@/lib/api'

type Inventario = {
  id: number
  codigo: string
  descricao: string | null
  deposito_id: number
  deposito_nome: string | null
  tipo: string
  status: string
  total_itens: number
  total_contados: number
  total_divergentes: number
  valor_divergencia: string | number
  iniciado_em: string | null
  finalizado_em: string | null
  created_at: string
}

const STATUS_CONFIG: Record<string, { label: string; cls: string; icon: any }> = {
  rascunho:     { label: 'Rascunho',      cls: 'bg-ink-100 text-ink-700',           icon: Clock },
  em_contagem:  { label: 'Em contagem',   cls: 'bg-blue-100 text-blue-700',         icon: Play },
  conferencia:  { label: 'Conferência',   cls: 'bg-amber-100 text-amber-700',       icon: AlertTriangle },
  finalizado:   { label: 'Finalizado',    cls: 'bg-emerald-100 text-emerald-700',   icon: CheckCircle2 },
  cancelado:    { label: 'Cancelado',     cls: 'bg-red-100 text-red-700',           icon: XCircle },
  aprovado:     { label: 'Aprovado',      cls: 'bg-emerald-100 text-emerald-700',   icon: CheckCircle2 },
}

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.rascunho
  const Icon = cfg.icon
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${cfg.cls}`}>
      <Icon size={11} /> {cfg.label}
    </span>
  )
}

export default function InventariosListPage() {
  const navigate = useNavigate()
  const [filtroStatus, setFiltroStatus] = useState<string>('')

  const { data: inventarios = [], isLoading } = useQuery<Inventario[]>({
    queryKey: ['inventarios', filtroStatus],
    queryFn: async () => {
      const params = filtroStatus ? { status: filtroStatus } : undefined
      return (await api.get('/inventarios', { params })).data
    },
  })

  const kpis = useMemo(() => {
    const ativos = inventarios.filter(i => ['em_contagem', 'conferencia'].includes(i.status)).length
    const rascunho = inventarios.filter(i => i.status === 'rascunho').length
    const divergTotal = inventarios.reduce((acc, i) => acc + Number(i.valor_divergencia || 0), 0)
    return { ativos, rascunho, divergTotal }
  }, [inventarios])

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardList size={24} /> Inventários
          </h1>
          <p className="text-ink-500 text-sm">
            Contagem física com bipagem. Bloqueia venda de produtos em contagem.
          </p>
        </div>
        <button
          onClick={() => navigate('/estoque/inventarios/novo')}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus size={16} /> Novo inventário
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="card card-body">
          <div className="text-xs text-ink-500 uppercase tracking-wide">Em andamento</div>
          <div className="text-2xl font-bold text-blue-600">{kpis.ativos}</div>
          <div className="text-xs text-ink-500">Bloqueando venda de produtos</div>
        </div>
        <div className="card card-body">
          <div className="text-xs text-ink-500 uppercase tracking-wide">Rascunhos</div>
          <div className="text-2xl font-bold text-ink-700">{kpis.rascunho}</div>
          <div className="text-xs text-ink-500">Aguardando iniciar</div>
        </div>
        <div className="card card-body">
          <div className="text-xs text-ink-500 uppercase tracking-wide">Divergência total</div>
          <div className="text-2xl font-bold text-amber-600">
            {formatMoney(kpis.divergTotal)}
          </div>
          <div className="text-xs text-ink-500">Valor absoluto em divergências</div>
        </div>
      </div>

      {/* Filtro */}
      <div className="card card-body flex items-center gap-3">
        <Filter size={14} className="text-ink-500" />
        <span className="text-sm text-ink-600">Status:</span>
        <select
          value={filtroStatus}
          onChange={e => setFiltroStatus(e.target.value)}
          className="input input-sm max-w-xs"
        >
          <option value="">Todos</option>
          <option value="rascunho">Rascunho</option>
          <option value="em_contagem">Em contagem</option>
          <option value="conferencia">Conferência</option>
          <option value="finalizado">Finalizado</option>
          <option value="cancelado">Cancelado</option>
        </select>
      </div>

      {/* Tabela */}
      <div className="card overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th>Código</th>
              <th>Descrição</th>
              <th>Depósito</th>
              <th>Tipo</th>
              <th>Status</th>
              <th className="text-right">Itens</th>
              <th className="text-right">Divergentes</th>
              <th className="text-right">Valor div.</th>
              <th>Criado</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={9} className="text-center text-ink-500 py-8">Carregando...</td></tr>
            )}
            {!isLoading && inventarios.length === 0 && (
              <tr><td colSpan={9} className="text-center text-ink-500 py-8">
                Nenhum inventário encontrado. <Link to="/estoque/inventarios/novo" className="text-brand-600 hover:underline">Criar o primeiro</Link>.
              </td></tr>
            )}
            {inventarios.map(inv => (
              <tr
                key={inv.id}
                onClick={() => navigate(`/estoque/inventarios/${inv.id}`)}
                className="cursor-pointer hover:bg-ink-50"
              >
                <td className="font-mono text-xs font-semibold">{inv.codigo}</td>
                <td>{inv.descricao || <span className="text-ink-400 italic">sem descrição</span>}</td>
                <td className="text-xs flex items-center gap-1">
                  <Warehouse size={11} /> {inv.deposito_nome || `#${inv.deposito_id}`}
                </td>
                <td className="text-xs capitalize">{inv.tipo}</td>
                <td><StatusBadge status={inv.status} /></td>
                <td className="text-right text-sm">
                  {inv.total_contados}/{inv.total_itens}
                </td>
                <td className="text-right text-sm">
                  {inv.total_divergentes > 0 ? (
                    <span className="text-amber-700 font-semibold">{inv.total_divergentes}</span>
                  ) : (
                    <span className="text-ink-400">0</span>
                  )}
                </td>
                <td className="text-right text-sm font-medium">
                  {Number(inv.valor_divergencia || 0) !== 0
                    ? formatMoney(Number(inv.valor_divergencia))
                    : <span className="text-ink-400">—</span>}
                </td>
                <td className="text-xs text-ink-500">{formatDateTime(inv.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
